import { CombinedAct } from '@shared/types/osi/acts';

/**
 * OSI-818: сколько подписанных общих актов показывать в свёрнутом списке.
 * Через полгода работы их будет 6+, и полный список с составами растягивает страницу.
 */
export const SIGNED_COMBINED_ACTS_LIMIT = 3;

/** Сравнение ISO-строк дат «новее — раньше»; пустое значение считается самым старым. */
const compareDesc = (a: string | null | undefined, b: string | null | undefined): number => {
  const left = a ?? '';
  const right = b ?? '';

  if (left === right) return 0;

  return left < right ? 1 : -1;
};

/**
 * OSI-818: подписанные общие акты — новые сверху: по периоду (actPeriod) убыв.,
 * при равном периоде — по дате подписи (signDt) убыв., затем по id убыв.
 * Исходный массив не меняется (он observable во view-model).
 */
export const sortSignedCombinedActs = (acts: CombinedAct[]): CombinedAct[] =>
  [...acts].sort((a, b) => compareDesc(a.actPeriod, b.actPeriod) || compareDesc(a.signDt, b.signDt) || b.id - a.id);

export interface VisibleSignedCombinedActs {
  /** Что показать сейчас. */
  visible: CombinedAct[];
  /** Сколько скрыто в свёрнутом виде (для «Показать ещё (K)»); 0 — кнопка не нужна. */
  hiddenCount: number;
  /** Нужна ли вообще кнопка «Показать ещё / Свернуть» (актов больше лимита). */
  hasMore: boolean;
}

/**
 * OSI-818: отбор видимых подписанных общих актов. Свёрнуто — последние `limit`
 * (после сортировки «новые сверху»), развёрнуто — все.
 */
export const getVisibleSignedCombinedActs = (
  acts: CombinedAct[],
  expanded: boolean,
  limit: number = SIGNED_COMBINED_ACTS_LIMIT
): VisibleSignedCombinedActs => {
  const sorted = sortSignedCombinedActs(acts);
  const hasMore = sorted.length > limit;

  if (expanded || !hasMore) {
    return { visible: sorted, hiddenCount: 0, hasMore };
  }

  return { visible: sorted.slice(0, limit), hiddenCount: sorted.length - limit, hasMore };
};

/**
 * OSI-818: форма множественного числа по правилам русского языка:
 * one — 1, 21, 31… (кроме 11); few — 2–4, 22–24… (кроме 12–14); many — остальное.
 * Своя функция, а не плюрализация i18next: в проекте i18next 20 (старый формат ключей
 * `_0/_1/_2`), и для казахской локали правила там не гарантированы. В kk все три формы
 * в словаре одинаковые («N үй»).
 */
export const getRuPluralForm = (count: number): 'one' | 'few' | 'many' => {
  const n = Math.abs(Math.trunc(count));
  const mod10 = n % 10;
  const mod100 = n % 100;

  if (mod10 === 1 && mod100 !== 11) return 'one';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few';

  return 'many';
};
